import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AutorenewIcon from '@mui/icons-material/Autorenew';
import Battery20Icon from '@mui/icons-material/Battery20';
import Battery50Icon from '@mui/icons-material/Battery50';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExploreIcon from '@mui/icons-material/Explore';
import InfoIcon from '@mui/icons-material/Info';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ModeOfTravelIcon from '@mui/icons-material/ModeOfTravel';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import NavigationIcon from '@mui/icons-material/Navigation';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { Avatar, List, ListItem, ListItemAvatar, ListItemText, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import { AnimatedSparkles } from '../../assets/animations';
import { RobotViewContext } from '../../context/RobotView';

const COLORS = {
  success: '#0E7F19',
  grey: 'grey',
  black: 'black',
};

const BatteryStatusIcon = ({ batteryStatus, chargeStatus }) => {
  if (batteryStatus === 1) {
    return <BatteryChargingFullIcon fontSize="medium" sx={{ color: COLORS.success }} />;
  }
  if (batteryStatus === 2) {
    return <BatteryAlertIcon fontSize="medium" color="error" />;
  }

  const BatteryIcon = chargeStatus <= 0.2 ? Battery20Icon :
    chargeStatus > 0.8 ? BatteryFullIcon : Battery50Icon;

  const color = chargeStatus <= 0.2 ? "error" :
    chargeStatus > 0.8 ? COLORS.success : "warning";

  return (
    <Stack alignItems={{ xs: '', md: 'center' }}>
      <BatteryIcon fontSize="medium" sx={{ color }} />
      {chargeStatus && (
        <Typography variant='caption' sx={{ fontWeight: chargeStatus > 0.8 ? 600 : 700, color: COLORS.grey }}>
          {`(${chargeStatus * 100}%)`}
        </Typography>
      )}
    </Stack>
  );
};

const RobotStatusWidget = ({ mqttNavInfo, isHeaderInfo = false, isMapInfo = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [rosStatus, setRosStatus] = useState('');
  const [autoPause, setAutoPause] = useState(null);
  const [locConfidence, setLocConfidence] = useState(null);
  const [navState, setNavState] = useState(null);
  const [relocStatus, setRelocStatus] = useState(null);
  const [currentAisle, setCurrentAisle] = useState(null);
  const [lastAisle, setLastAisle] = useState(null);
  const [batteryStatus, setBatteryStatus] = useState(null);
  const [batteryVoltage, setBatteryVoltage] = useState(null);
  const [rawRobotState, setRawRobotState] = useState(null);
  const [statusData, setStatusData] = useState([]);

  const {
    rawRobotStatus,
    robotFloor,
    connectionStatusMsg,
  } = useContext(RobotViewContext);


  // Hooks
  useEffect(() => {
    let newStatusData = [];

    if (isHeaderInfo) {
      newStatusData = newStatusData.concat([
        { name: 'ROS', key: 'ros_status' },
        { name: 'Battery', key: 'battery_status' },
        { name: 'Internal', key: 'state' },
        { name: 'Autorecovery', key: 'autorecovery' }
      ])
    }
    if (isMapInfo) {
      newStatusData = newStatusData.concat([
        { name: 'Navigation', key: 'nav_state' },
        { name: 'Confidence', key: 'loc_conf' },
        { name: 'Relocalization', key: 'reloc_status' },
        { name: 'Current Aisle', key: 'current_aisle' },
        { name: 'Last Aisle', key: 'last_aisle' },
        { name: 'Floor', key: 'robot_floor' }
      ])
    }

    setStatusData(newStatusData);

  }, [isHeaderInfo, isMapInfo]);

  useEffect(() => {
    if (mqttNavInfo.loc_conf) setLocConfidence(`${mqttNavInfo.loc_conf * 100}%`);
    if (mqttNavInfo?.nav_criteria) setNavState(mqttNavInfo.nav_criteria);
  }, [mqttNavInfo]);

  useEffect(() => {
    if (!rawRobotStatus) return;

    const updateRobotStatus = () => {
      setAutoPause(rawRobotStatus.robot_paused_auto_alert);
      setRelocStatus(rawRobotStatus.visual_localization_system?.status);
      setCurrentAisle(rawRobotStatus.current_aisle === '0' ? '-' : rawRobotStatus.current_aisle);

      if (rawRobotStatus.aisles_to_process?.length > 0) {
        setLastAisle(rawRobotStatus.aisles_to_process.slice(-1)[0]);
      } else if (rawRobotStatus.aisles_processed?.length > 0) {
        setLastAisle(rawRobotStatus.aisles_processed.slice(-1)[0]);
      } else {
        setLastAisle('-');
      }

      setBatteryStatus(rawRobotStatus.battery_status);
      setBatteryVoltage(rawRobotStatus.battery);
      setRawRobotState(rawRobotStatus.state);
    };

    updateRobotStatus();
  }, [rawRobotStatus]);

  useEffect(() => {
    if (connectionStatusMsg.program_status) {
      setRosStatus(connectionStatusMsg.program_status);
    }
  }, [connectionStatusMsg]);


  // Handlers
  const getRosStatusInfo = (status) => {
    const statusMap = {
      ready: {
        icon: <CheckCircleIcon fontSize="medium" sx={{ color: COLORS.success }} />,
        description: t('overseer_app.widget.software_ready', 'Software ready and session planned')
      },
      off: {
        icon: <CancelIcon color="error" fontSize="medium" />,
        description: t('overseer_app.widget.software_off', 'Software off')
      },
      turning_on: {
        icon: <AutorenewIcon fontSize="medium" sx={{ color: COLORS.success }} />,
        description: t('overseer_app.widget.turning_on', 'Turning on software')
      },
      turning_off: {
        icon: <AutorenewIcon color="error" fontSize="medium" />,
        description: t('overseer_app.widget.turning_off', 'Turning off software')
      }
    };

    return statusMap[status] || {
      icon: <QuestionMarkIcon fontSize="medium" />,
      description: t('overseer_app.widget.unknown', 'Unknown')
    };
  };

  const getStatusIcon = (key) => {
    const iconMap = {
      reloc_status: <ExploreIcon fontSize="medium" color="info" />,
      last_aisle: <ModeOfTravelIcon fontSize="medium" color="info" />,
      current_aisle: <LocationOnIcon fontSize="medium" color="info" />,
      state: <InfoIcon fontSize="medium" color="info" />,
      nav_state: <NavigationIcon fontSize="medium" color="info" />,
      loc_conf: <MyLocationIcon fontSize="medium" color="info" />,
      robot_floor: <IconComponent style={{ fontSize: 24, color: theme.palette.info.main }} iconName={'layers'} />
    };

    return iconMap[key];
  };

  const getNavigationInfo = (item) => {
    let icon, title, value, description;
    const valuesMap = {
      ros_status: rosStatus,
      auto_pause: autoPause,
      loc_conf: locConfidence,
      nav_state: navState,
      reloc_status: relocStatus,
      current_aisle: currentAisle,
      last_aisle: lastAisle,
      state: rawRobotState,
      autorecovery: rawRobotStatus?.autorecovery?.status,
      robot_floor: robotFloor
    }

    if (item.key === 'ros_status') {
      const rosInfo = getRosStatusInfo(rosStatus);
      title = 'ROS';
      icon = rosInfo.icon;
      description = rosInfo.description;
    } else if (item.key === 'battery_status') {
      title = t(`overseer_app.widget.${item.key}`, item.name);
      value = batteryVoltage && `${batteryVoltage} V`;
      icon = <BatteryStatusIcon batteryStatus={batteryStatus} chargeStatus={rawRobotStatus?.charge_state} />;
    } else if (item.key === 'autorecovery') {
      const autorecoveryInfo = rawRobotStatus?.autorecovery;
      title = t('overseer_app.general.autorecovery', 'Autorecovery');
      if (autorecoveryInfo?.status && autorecoveryInfo?.status !== 'idle') {
        icon = <Avatar sizes='10px' sx={(theme) => ({ backgroundColor: theme.palette.warning.main, width: 25, height: 25 })}>
          <AnimatedSparkles size={14} color="white" />
        </Avatar>;
      } else {
        icon = <Avatar sizes='10px' sx={(theme) => ({ backgroundColor: theme.palette.gray.main, width: 25, height: 25 })}>
          <IconComponent iconName="sparkles" style={{ fontSize: 12, color: 'white' }} />
        </Avatar>;
      }
      description = autorecoveryInfo?.init_timestamp;
      value = autorecoveryInfo?.init_timestamp ? `${autorecoveryInfo?.status} (${autorecoveryInfo?.init_timestamp})` : autorecoveryInfo?.status;
    } else {
      title = t(`overseer_app.widget.${item.key}`, item.name);
      value = item.key && valuesMap[item.key] ?
        valuesMap[item.key].toString() +
        (item.key === 'nav_state' && !valuesMap['auto_pause'] ? ` (${t('overseer_app.widget.auto_pause')})` : '')
        : null;
      icon = getStatusIcon(item.key);
    }

    return { icon, title, value, description };
  };

  return (
    <List dense component={Stack} direction={{ xs: 'column', md: 'row' }}>
      {statusData.map((item, index) => {
        const { icon, title, value, description } = getNavigationInfo(item);
        return (
          <ListItem key={`${item.name}-${index}`}>
            {/* Icon */}
            <ListItemAvatar sx={{ minWidth: '40px' }}>
              {description ? <Tooltip title={description}>{icon}</Tooltip> : icon}
            </ListItemAvatar>
            {/* Title and subtitle*/}
            <ListItemText primary={title} secondary={value} primaryTypographyProps={{ fontWeight: 'bold' }} />
          </ListItem>
        );
      })}
    </List>
  );
};

export default RobotStatusWidget;
